import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, Image as ImageIcon } from "lucide-react";

export const BlogEditor = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    author: "",
    category: "Technology",
    image: "",
    read_time: "5 min read",
    status: "draft",
  });

  const categories = [
    "Technology",
    "Innovation",
    "Mobility",
    "Business",
    "Sustainability",
    "Industry News",
  ];

  const fetchPost = useCallback(async () => {
    if (!id) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}api/blog/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.title || "",
          slug: data.slug || "",
          excerpt: data.excerpt || "",
          content: data.content || "",
          author: data.author || "",
          category: data.category || "Technology",
          image: data.image || "",
          read_time: data.read_time || "5 min read",
          status: data.status || "draft",
        });
      } else {
        toast.error("Failed to load post");
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error("Error fetching post:", error);
      toast.error("Failed to load post");
      navigate("/admin/dashboard");
    } finally {
      setFetching(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    if (isEditMode) {
      fetchPost();
    }
  }, [token, navigate, isEditMode, fetchPost]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "title") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData((prev) => ({
        ...prev,
        slug,
      }));
    }
  };

  const handleSubmit = async (e, publishStatus = null) => {
    e.preventDefault();
    setLoading(true);

    const submitData = {
      ...formData,
      status: publishStatus || formData.status,
    };

    try {
      const url = isEditMode
        ? `${process.env.REACT_APP_BACKEND_URL}api/blog/${id}`
        : `${process.env.REACT_APP_BACKEND_URL}api/blog`;

      const method = isEditMode ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      const contentType = response.headers.get("content-type") || "";
      let body;

      if (contentType.includes("application/json")) {
        body = await response.json();
      } else {
        body = await response.text();
      }

      if (!response.ok) {
        const message =
          (body && body.detail) ||
          (typeof body === "string" && body) ||
          "Failed to save post";
        toast.error(message);
        return;
      }

      const successMessage = isEditMode
        ? "Post updated successfully!"
        : "Post created successfully!";
      toast.success(successMessage);
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error("Failed to save post");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading post...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="ghost" size="sm" className="text-gray-600">
                  <ArrowLeft className="mr-2" size={18} />
                  Back
                </Button>
              </Link>
              <h1 className="text-xl font-bold text-[#1B4F72]">
                {isEditMode ? "Edit Post" : "Create New Post"}
              </h1>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={loading}
                onClick={(e) => handleSubmit(e, "draft")}
                className="border-gray-300"
              >
                Save as Draft
              </Button>
              <Button
                type="button"
                disabled={loading}
                onClick={(e) => handleSubmit(e, "published")}
                className="bg-[#138808] hover:bg-[#0F6806] text-white"
              >
                <Eye className="mr-2" size={18} />
                {loading ? "Saving..." : "Publish"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 space-y-6">
                  {/* Title */}
                  <div className="space-y-2">
                    <Label htmlFor="title" className="text-[#1B4F72] font-semibold">
                      Title *
                    </Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter post title..."
                      required
                      className="text-lg border-gray-300 focus:border-[#FF9933] focus:ring-[#FF9933]"
                    />
                  </div>

                  {/* Slug */}
                  <div className="space-y-2">
                    <Label htmlFor="slug" className="text-[#1B4F72] font-semibold">
                      URL Slug *
                    </Label>
                    <Input
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      placeholder="url-friendly-slug"
                      required
                      className="border-gray-300 focus:border-[#FF9933] focus:ring-[#FF9933]"
                    />
                    <p className="text-sm text-gray-500">
                      Auto-generated from title. Edit if needed.
                    </p>
                  </div>

                  {/* Excerpt */}
                  <div className="space-y-2">
                    <Label htmlFor="excerpt" className="text-[#1B4F72] font-semibold">
                      Excerpt *
                    </Label>
                    <Textarea
                      id="excerpt"
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleChange}
                      placeholder="Brief summary of the post (shown in listings)..."
                      required
                      rows={3}
                      className="border-gray-300 focus:border-[#FF9933] focus:ring-[#FF9933] resize-none"
                    />
                    <p className="text-sm text-gray-500">
                      {formData.excerpt.length}/500 characters
                    </p>
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <Label htmlFor="content" className="text-[#1B4F72] font-semibold">
                      Content *
                    </Label>
                    <Textarea
                      id="content"
                      name="content"
                      value={formData.content}
                      onChange={handleChange}
                      placeholder="Write your blog post content here... You can use basic markdown."
                      required
                      rows={15}
                      className="border-gray-300 focus:border-[#FF9933] focus:ring-[#FF9933] resize-none font-mono text-sm"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Post Details */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 space-y-5">
                  <h3 className="font-bold text-[#1B4F72] text-lg border-b pb-3">
                    Post Details
                  </h3>

                  {/* Author */}
                  <div className="space-y-2">
                    <Label htmlFor="author" className="text-[#1B4F72] font-semibold">
                      Author *
                    </Label>
                    <Input
                      id="author"
                      name="author"
                      value={formData.author}
                      onChange={handleChange}
                      placeholder="Author name"
                      required
                      className="border-gray-300 focus:border-[#FF9933] focus:ring-[#FF9933]"
                    />
                  </div>

                  {/* Category */}
                  <div className="space-y-2">
                    <Label htmlFor="category" className="text-[#1B4F72] font-semibold">
                      Category *
                    </Label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#FF9933] focus:ring-1 focus:ring-[#FF9933] focus:outline-none"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Read Time */}
                  <div className="space-y-2">
                    <Label htmlFor="read_time" className="text-[#1B4F72] font-semibold">
                      Read Time
                    </Label>
                    <Input
                      id="read_time"
                      name="read_time"
                      value={formData.read_time}
                      onChange={handleChange}
                      placeholder="5 min read"
                      className="border-gray-300 focus:border-[#FF9933] focus:ring-[#FF9933]"
                    />
                  </div>

                  {/* Status */}
                  <div className="space-y-2">
                    <Label htmlFor="status" className="text-[#1B4F72] font-semibold">
                      Status
                    </Label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#FF9933] focus:ring-1 focus:ring-[#FF9933] focus:outline-none"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </CardContent>
              </Card>

              {/* Featured Image */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-bold text-[#1B4F72] text-lg border-b pb-3">
                    Featured Image
                  </h3>

                  <div className="space-y-2">
                    <Label htmlFor="image" className="text-[#1B4F72] font-semibold">
                      Image URL *
                    </Label>
                    <Input
                      id="image"
                      name="image"
                      value={formData.image}
                      onChange={handleChange}
                      placeholder="https://example.com/image.jpg"
                      required
                      className="border-gray-300 focus:border-[#FF9933] focus:ring-[#FF9933]"
                    />
                  </div>

                  {/* Image Preview */}
                  {formData.image && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-600 mb-2">Preview:</p>
                      <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border">
                        <img
                          src={formData.image}
                          alt="Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="absolute inset-0 items-center justify-center bg-gray-100 hidden">
                          <div className="text-center text-gray-400">
                            <ImageIcon className="mx-auto mb-2" size={32} />
                            <p className="text-sm">Invalid image URL</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Save Button (Mobile) */}
              <div className="lg:hidden">
                <Button
                  type="button"
                  disabled={loading}
                  onClick={(e) => handleSubmit(e, 'published')}
                  className="w-full bg-[#138808] hover:bg-[#0F6806] text-white py-6"
                >
                  <Save className="mr-2" size={18} />
                  {loading ? 'Saving...' : 'Publish Post'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
